import { useMemo, useEffect } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import { Maximize2 } from "lucide-react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { COMUNE_COORDS } from "../lib/constants";

function countIcon(count) {
  return L.divIcon({
    className: "",
    html: `<div style="background:#8C3A27;color:#fff;width:32px;height:32px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);display:flex;align-items:center;justify-content:center;box-shadow:0 2px 6px rgba(0,0,0,.3);border:2px solid #fff"><span style="transform:rotate(45deg);font-weight:700;font-size:12px;font-family:'Plus Jakarta Sans',sans-serif">${count}</span></div>`,
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    popupAnchor: [0, -28],
  });
}

function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    const t = setTimeout(() => {
      map.invalidateSize();
      if (points.length === 1) map.setView(points[0], 11);
      else if (points.length > 1) map.fitBounds(points, { padding: [40, 40], maxZoom: 12 });
    }, 200);
    return () => clearTimeout(t);
  }, [map, points]);
  return null;
}

export default function MiniBooksMap({ books, height = 280, onExpand, className = "" }) {
  const grouped = useMemo(() => {
    const g = {};
    for (const b of books) {
      if (!COMUNE_COORDS[b.comune]) continue;
      (g[b.comune] = g[b.comune] || []).push(b);
    }
    return g;
  }, [books]);

  const entries = Object.entries(grouped);
  const key = entries.map(([c, l]) => `${c}:${l.length}`).join("|");
  const points = useMemo(() => entries.map(([c]) => COMUNE_COORDS[c]), [key]); // eslint-disable-line react-hooks/exhaustive-deps
  if (entries.length === 0) return null;

  return (
    <div className={`relative rounded-2xl overflow-hidden border border-border ${className}`} style={{ height }} data-testid="home-mini-map">
      {onExpand && (
        <button
          type="button"
          onClick={onExpand}
          data-testid="mini-map-expand"
          className="absolute top-3 right-3 z-[500] inline-flex items-center gap-1.5 h-9 px-3 rounded-full bg-background/95 border border-border text-xs font-medium shadow-sm hover:bg-background transition-colors"
        >
          <Maximize2 className="w-3.5 h-3.5" />Schermo intero
        </button>
      )}
      <MapContainer center={points[0]} zoom={10} style={{ height: "100%", width: "100%" }} scrollWheelZoom={false}>
        <TileLayer attribution='&copy; OpenStreetMap' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
        <FitBounds points={points} />
        {entries.map(([comune, list]) => (
          <Marker key={comune} position={COMUNE_COORDS[comune]} icon={countIcon(list.length)}>
            <Popup>
              <div style={{ minWidth: 160 }}>
                <p style={{ fontWeight: 700, marginBottom: 6 }}>{comune} · {list.length} {list.length === 1 ? "libro" : "libri"}</p>
                <div style={{ display: "flex", flexDirection: "column", gap: 4, maxHeight: 180, overflowY: "auto" }}>
                  {list.slice(0, 10).map((b) => (
                    <a key={b.id} href={`/libro/${b.id}`} style={{ fontSize: 12, lineHeight: 1.3, textDecoration: "none", color: "#8C3A27" }}>
                      <strong>{b.title}</strong> <span style={{ color: "#8a7f76" }}>· {b.author}</span>
                    </a>
                  ))}
                </div>
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
}
